import mbxGeo from '@mapbox/mapbox-sdk/services/geocoding';
import {
  isPostcode,
  MAPBOX_QUERY_TYPES,
  CountriesList,
  createMapboxClient,
  toRadians,
} from 'platform/utilities/facilities-and-mapbox';
import { mapboxToken } from '../utils/mapboxToken';
import {
  CLEAR_SEARCH_RESULTS,
  GEOCODE_CLEAR_ERROR,
  GEOCODE_COMPLETE,
  GEOCODE_FAILED,
  GEOCODE_STARTED,
  GEOLOCATE_USER,
  LOCATION_INPUT_INVALID,
  MAP_MOVED,
  MOBILE_MAP_PIN_SELECTED,
  SEARCH_FAILED,
  SEARCH_QUERY_UPDATED,
} from './actionTypes';
import {
  BOUNDING_RADIUS,
  EXPANDED_BOUNDING_RADIUS,
  LocationType,
  MAX_SEARCH_AREA_RADIUS_MILES,
  MIN_RADIUS_EXP,
  MIN_RADIUS_NCA,
} from '../constants';
import { distBetween, radiusFromBoundingBox } from '../utils/facilityDistance';
import { updateSearchQuery } from './search';

const mbxClient = mbxGeo(createMapboxClient(mapboxToken));

export const constructBounds = ({
  facilityType,
  longitude,
  latitude,
  expandedRadius,
  mapAspectRatio = 1,
}) => {
  const PPMSTypes = [
    LocationType.CC_PROVIDER,
    LocationType.URGENT_CARE_PHARMACIES,
  ];

  // Use latitude to make the longitude changes relative
  const latitudeModifier = Math.cos(toRadians(latitude));

  // Default to the normal bounding radius
  let searchExpandedLat = expandedRadius
    ? EXPANDED_BOUNDING_RADIUS
    : BOUNDING_RADIUS;
  // Default case uses raw degree offsets (not converted from miles), so scale
  // E-W directly by mapAspectRatio without the latitude modifier.
  let searchExpandedLon = searchExpandedLat * mapAspectRatio;

  // Do not use for PPMS since it does not use the bounding box
  // latitude is divided into 111.11 km or ~69 mi
  if (facilityType === LocationType.CEMETERY) {
    searchExpandedLat = MIN_RADIUS_NCA / 69;
    // Mile-based paths need the latitude modifier to convert miles → degrees.
    searchExpandedLon =
      (MIN_RADIUS_NCA * mapAspectRatio) / Math.abs(latitudeModifier * 69);
  } else if (!PPMSTypes.includes(facilityType)) {
    searchExpandedLat = MIN_RADIUS_EXP / 69;
    searchExpandedLon =
      (MIN_RADIUS_EXP * mapAspectRatio) / Math.abs(latitudeModifier * 69);
  }

  // Since we do this construction for a box we use absolute value above
  return [
    longitude - searchExpandedLon,
    latitude - searchExpandedLat,
    longitude + searchExpandedLon,
    latitude + searchExpandedLat,
  ];
};

// True when a geocoded bounding box is too large to search usefully (a whole
// state). Measures the larger half-span of the bbox in miles so city-sized
// regions (Washington, DC; metro Manila; territories) still search, while big
// states do not. Missing bbox → not too large (let it search) (CMS #22751).
export const isAreaTooLarge = bbox => {
  if (!Array.isArray(bbox) || bbox.length < 4) {
    return false;
  }
  const [west, south, east, north] = bbox;
  const centerLat = (south + north) / 2;
  const centerLng = (west + east) / 2;
  const nsHalf = distBetween(centerLat, centerLng, north, centerLng);
  const ewHalf = distBetween(centerLat, centerLng, centerLat, west);
  return Math.max(nsHalf, ewHalf) > MAX_SEARCH_AREA_RADIUS_MILES;
};

// True when a Mapbox feature the address autosuggest already fetched carries
// everything processFeaturesBBox needs, so genBBoxFromAddress can skip its
// forwardGeocode entirely. Anything less and we ask Mapbox again rather than
// search a half-resolved place (CMS #20560).
export const canReuseFeature = feature =>
  Array.isArray(feature?.center) &&
  feature.center.length >= 2 &&
  Array.isArray(feature?.geometry?.coordinates) &&
  feature.geometry.coordinates.length >= 2 &&
  typeof feature?.id === 'string' &&
  feature.id.length > 0;

export const processFeaturesBBox = (
  query,
  features,
  dispatch,
  { expandedRadius = false, mapAspectRatio = 1, validateOnly = false } = {},
) => {
  // We never use anything but the first feature
  const firstFeature = features[0] || {};

  // No usable geocoder match — surface as an invalid-location field error and
  // clear any prior results so they don't linger (CMS #22751).
  if (!firstFeature?.center || !firstFeature.geometry || !firstFeature.id) {
    dispatch({ type: LOCATION_INPUT_INVALID });
    dispatch({ type: CLEAR_SEARCH_RESULTS });
    return;
  }

  // A region match is only rejected when it's too large to search usefully
  // (a whole state). City-sized regions — Washington, DC; metro Manila;
  // territories — fall through and search normally (CMS #22751).
  if (
    firstFeature.place_type?.[0] === 'region' &&
    isAreaTooLarge(firstFeature.bbox)
  ) {
    dispatch({ type: LOCATION_INPUT_INVALID });
    dispatch({ type: CLEAR_SEARCH_RESULTS });
    return;
  }

  dispatch({ type: GEOCODE_COMPLETE, payload: [] });

  // A validate-only call only checks whether the location is real while
  // another field (facility/service type) is still invalid — it must not
  // kick off an actual search before the rest of the form is valid
  // (CMS #24836).
  if (validateOnly) {
    return;
  }

  // Doesn't always have context, but if it does and it's a zipcode, use it
  const zip = firstFeature.context?.find(v => v.id.includes('postcode')) || {};
  const zipCode = zip.text || firstFeature.place_name;

  // used to generate the surrounding box
  const coordinates = firstFeature.center;
  const [longitude, latitude] = coordinates;

  // used to generate the search coordinates
  const [lng, lat] = firstFeature.geometry.coordinates;

  const minBounds = constructBounds({
    facilityType: query?.facilityType,
    longitude,
    latitude,
    expandedRadius,
    mapAspectRatio,
  });

  // radiusFromBoundingBox returns an array with the first element being the
  // bounding box from the minBounds but the second is either that values or
  // a minimum radius value based on the facility type
  const [, radiusOrMinRadius] = radiusFromBoundingBox(
    [{ ...firstFeature, bbox: minBounds }],
    query?.facilityType,
  );

  dispatch({
    type: SEARCH_QUERY_UPDATED,
    payload: {
      ...query,
      radius: radiusOrMinRadius,
      context: zipCode,
      id: Date.now(),
      inProgress: true,
      position: {
        latitude,
        longitude,
      },
      searchCoords: {
        lat,
        lng,
      },
      bounds: minBounds,
      zoomLevel: firstFeature.id.split('.')[0] === 'region' ? 7 : 9,
      currentPage: 1,
      mapBoxQuery: {
        placeName: firstFeature.place_name,
        placeType: firstFeature.place_type?.[0],
      },
      searchArea: null,
    },
  });
};

/**
 * Calculates a bounding box (±BOUNDING_RADIUS°) centering on the current
 * address string as typed by the user.
 *
 * @param {Object<T>} query Current searchQuery state (`searchQuery.searchString` at a minimum)
 * @param {Object} [options]
 * @param {boolean} [options.expandedRadius] Widen the minimum search radius,
 * used for benefits searches with no service type selected.
 * @param {number} [options.mapAspectRatio] Map width÷height, used to stretch
 * the bounding box E-W so it matches what the map actually shows.
 * @param {boolean} [options.validateOnly] When true, only checks whether the
 * location geocodes successfully — used to check location validity on a
 * blocked submit (facility/service type still invalid) without starting a real
 * search or surfacing a generic search-failure error (CMS #24836).
 * @param {Object} [options.feature] A Mapbox geocoding feature the address
 * autosuggest already fetched for this exact `searchString` — the suggestion the
 * user picked, or the first one offered when they submitted without picking.
 * When usable it replaces the forwardGeocode below (CMS #20560).
 * @returns {Function<T>} A thunk for Redux to process OR a failure action object on bad input
 */
export const genBBoxFromAddress = (
  query,
  {
    expandedRadius = false,
    mapAspectRatio = 1,
    validateOnly = false,
    feature = null,
  } = {},
) => {
  // Prevent empty search request to Mapbox, which would result in error, and
  // clear results list to respond with message of no facilities found.
  if (!query.searchString) {
    return {
      type: SEARCH_FAILED,
      error: 'Empty search string/address. Search cancelled.',
    };
  }

  const processOptions = { expandedRadius, mapAspectRatio, validateOnly };

  // The autosuggest already resolved this string, so search the place the user
  // actually saw instead of spending a second round-trip re-resolving it. The
  // two calls differ only in `autocomplete` — which can change *which* feature
  // comes back first, not the fields we read off it — so reusing the picked one
  // is both cheaper and truer to the selection (CMS #20560).
  if (canReuseFeature(feature)) {
    return dispatch => {
      dispatch({ type: GEOCODE_STARTED });
      processFeaturesBBox(query, [feature], dispatch, processOptions);
    };
  }

  return dispatch => {
    dispatch({ type: GEOCODE_STARTED });

    // commas can be stripped from query if Mapbox is returning unexpected results
    let types = MAPBOX_QUERY_TYPES;

    if (isPostcode(query.searchString?.trim() || '')) {
      types = ['postcode'];
    }

    try {
      return mbxClient
        .forwardGeocode({
          countries: CountriesList,
          types,
          autocomplete: false, // set this to true when build the predictive search UI (feature-flipped)
          query: query.searchString,
          proximity: 'ip',
        })
        .send()
        .then(({ body: { features } }) =>
          processFeaturesBBox(query, features, dispatch, processOptions),
        )
        .catch(_e => {
          // A validate-only check can't tell "location is bad" apart from
          // "geocoder had an outage" — and since no real search was going to
          // run yet, there's nothing to surface a search-failure error for
          // (CMS #24836).
          if (validateOnly) {
            return;
          }
          dispatch({ type: GEOCODE_FAILED });
          dispatch({ type: SEARCH_FAILED, error: { type: 'mapBox' } });
        });
    } catch (_e) {
      if (!validateOnly) {
        dispatch({ type: GEOCODE_FAILED });
        dispatch({ type: SEARCH_FAILED, error: { type: 'mapBox' } });
      }
      return undefined;
    }
  };
};

export const clearGeocodeError = () => async dispatch => {
  dispatch({ type: GEOCODE_CLEAR_ERROR });
};

/**
 * Separated out from genSearchAreaFromCenter for unit testing
 */
export const sendUpdatedSearchQuery = (
  dispatch,
  features,
  lat,
  lng,
  currentBounds,
) => {
  const zip = features[0].context.find(v => v.id.includes('postcode')) || {};
  const location = zip.text || features[0].place_name;

  // Radius is the larger of the N-S and E-W half-spans, so the search covers
  // the full rectangular visible map area (portrait → nsHalf, landscape → ewHalf).
  const [west, , , north] = currentBounds;
  const nsHalf = distBetween(lat, lng, north, lng);
  const ewHalf = distBetween(lat, lng, lat, west);
  const radius = Math.max(nsHalf, ewHalf);

  dispatch({
    type: SEARCH_QUERY_UPDATED,
    payload: {
      radius,
      searchString: location,
      context: location,
      searchArea: {
        locationString: location,
        locationCoords: {
          lng,
          lat,
        },
      },
      mapBoxQuery: {
        placeName: features[0].place_name,
        placeType: features[0].place_type[0],
      },
      searchCoords: null,
      bounds: currentBounds,
      position: {
        latitude: lat,
        longitude: lng,
      },
    },
  });
};

/**
 * Calculates a human readable location (address, zip, city, state) updated
 * from the coordinates center of the map
 */
export const genSearchAreaFromCenter = query => {
  const { lat, lng, currentMapBoundsDistance, currentBounds } = query;

  return dispatch => {
    if (currentMapBoundsDistance > 500) {
      dispatch({ type: GEOCODE_FAILED });
      dispatch({ type: SEARCH_FAILED, error: { type: 'mapBox' } });
    } else {
      const types = MAPBOX_QUERY_TYPES;

      try {
        mbxClient
          .reverseGeocode({
            countries: CountriesList,
            types,
            query: [lng, lat],
          })
          .send()
          .then(({ body: { features } }) => {
            sendUpdatedSearchQuery(dispatch, features, lat, lng, currentBounds);
          })
          .catch(_ => {
            dispatch({ type: GEOCODE_FAILED });
            dispatch({ type: SEARCH_FAILED, error: { type: 'mapBox' } });
          });
      } catch (_) {
        dispatch({ type: GEOCODE_FAILED });
        dispatch({ type: SEARCH_FAILED, error: { type: 'mapBox' } });
      }
    }
  };
};

const getLocationData = async (longitude, latitude) => {
  return mbxClient
    .reverseGeocode({
      query: [longitude, latitude],
      types: ['address'],
    })
    .send();
};

/**
 * Generates search criteria from lat/long geocoordinates.
 */
export const searchCriteriaFromCoords = (response, longitude, latitude) => {
  const { features } = response.body;
  const placeName = features[0].place_name;
  const coordinates = features[0].center;

  return {
    bounds: features[0].bbox || [
      coordinates[0] - BOUNDING_RADIUS,
      coordinates[1] - BOUNDING_RADIUS,
      coordinates[0] + BOUNDING_RADIUS,
      coordinates[1] + BOUNDING_RADIUS,
    ],
    searchString: placeName,
    position: { longitude, latitude },
  };
};

// Custom sentinels outside the GeolocationPositionError range (1=PERMISSION_DENIED,
// 2=POSITION_UNAVAILABLE, 3=TIMEOUT). Each gets its own modal copy in
// components/search-form/index.jsx so users can tell these failure modes apart —
// the remedy differs (retry coords vs. retry network vs. fix permissions).
//
// REVERSE_GEOCODE_FAILED: Browser returned coords successfully but Mapbox can't
// turn them into an address — either the response had no features (e.g., middle
// of the ocean) or Mapbox replied with an HTTP error (e.g., 422 out-of-range).
// User fix: try a different city/zip.
export const REVERSE_GEOCODE_FAILED = 4;
// GEOCODE_SERVICE_UNAVAILABLE: We never heard back from Mapbox — DNS block, ad
// blocker, captive portal, offline, etc. The SDK rejects with no `statusCode`.
// User fix: check connection / retry later.
export const GEOCODE_SERVICE_UNAVAILABLE = 5;

// `fetchLocationData` is injectable so tests can stub the Mapbox reverse-geocode
// call without going to the network. Production callers omit it.
export const geolocateUser =
  (fetchLocationData = getLocationData) =>
  async dispatch => {
    const GEOLOCATION_TIMEOUT = 10000;

    if (navigator?.geolocation?.getCurrentPosition) {
      dispatch({ type: GEOLOCATE_USER });

      navigator.geolocation.getCurrentPosition(
        async currentPosition => {
          const { coords } = currentPosition;

          try {
            const locationData = await fetchLocationData(
              coords.longitude,
              coords.latitude,
            );

            if (!locationData?.body?.features?.[0]?.place_name) {
              dispatch({ type: GEOCODE_FAILED, code: REVERSE_GEOCODE_FAILED });
              return;
            }

            const query = searchCriteriaFromCoords(
              locationData,
              coords.longitude,
              coords.latitude,
            );

            dispatch({ type: GEOCODE_COMPLETE });
            dispatch(updateSearchQuery(query));
          } catch (e) {
            // @mapbox/mapbox-sdk MapiError carries a numeric `statusCode` for
            // HTTP responses (4xx/5xx) and omits it for transport-layer
            // failures (DNS block, offline, AbortError). Distinguishing these
            // lets us show the user a useful diagnosis.
            const code =
              typeof e?.statusCode === 'number'
                ? REVERSE_GEOCODE_FAILED
                : GEOCODE_SERVICE_UNAVAILABLE;
            dispatch({ type: GEOCODE_FAILED, code });
          }
        },
        e => {
          dispatch({ type: GEOCODE_FAILED, code: e.code });
        },
        { timeout: GEOLOCATION_TIMEOUT },
      );
    } else {
      dispatch({ type: GEOCODE_FAILED, code: -1 });
    }
  };

export const mapMoved = currentRadius => ({
  type: MAP_MOVED,
  currentRadius,
});

export const selectMobileMapPin = data => {
  return {
    type: MOBILE_MAP_PIN_SELECTED,
    payload: data,
  };
};
